import { z } from 'zod';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from './api';
import { DASHBOARD_QUERY_KEY } from './workouts';
import {
  DISCIPLINE_ORDER,
  type DisciplineKey,
} from './disciplineColors';

/**
 * Cliente HTTP + hooks para los endpoints extendidos del dashboard (M2.16).
 *
 * Este módulo NO reemplaza `lib/workouts.ts::useDashboard` — que sigue siendo
 * la fuente del `GET /api/dashboard` con `week`, `nextWorkout`, `strava`,
 * `load`. Aquí añadimos:
 *
 *  - `DashboardKpisSchema` (bloque opcional incluido por el backend dentro
 *    de la respuesta de `/api/dashboard`; la UI lo lee del objeto data).
 *  - `getDisciplineHours` + `useDisciplineHours` para `GET /api/dashboard/discipline-hours`.
 *  - `getDashboardAlerts` + `useDashboardAlerts` para `GET /api/dashboard/alerts`.
 *
 * Se mantiene el patrón de los demás clientes: schemas Zod source-of-truth,
 * helpers `getJson` propios (mismo mapeo de errores que workouts.ts), hooks
 * de TanStack Query con query keys agrupadas bajo `['dashboard', ...]`.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Schemas: KPIs (parte del response de /api/dashboard, opcional)
// ---------------------------------------------------------------------------

/**
 * Bloque `kpis` añadido al `GET /api/dashboard` por el backend en M2.16.
 * Todos los campos pueden ser `null` cuando no hay datos suficientes — la UI
 * decide por chip si renderizarlo o no.
 */
export const DashboardNextARaceSchema = z.object({
  name: z.string(),
  dateISO: z.string(),
  daysUntil: z.number().int(),
});
export type DashboardNextARace = z.infer<typeof DashboardNextARaceSchema>;

/**
 * M2.32.D — Aviso de imbalance de disciplinas. null si todas las disciplinas
 * core están dentro del umbral (≤7d sin tocar).
 */
export const ImbalanceWarnSchema = z.object({
  discipline: z.enum(['swim', 'bike', 'run']),
  days: z.number().int().positive(),
});
export type ImbalanceWarn = z.infer<typeof ImbalanceWarnSchema>;

export const DashboardKpisSchema = z.object({
  ctlRamp7d: z.number().nullable(),
  atl: z.number().nullable(),
  ctl: z.number().nullable(),
  tsb: z.number().nullable(),
  monotony7d: z.number().nullable(),
  strainWeek: z.number().nullable(),
  acwr: z.number().nullable(),
  compliance28d: z.number().nullable(),
  daysSinceLastOff: z.number().int().nonnegative(),
  daysSinceLastSwim: z.number().int().nullable(),
  daysSinceLastBike: z.number().int().nullable(),
  daysSinceLastRun: z.number().int().nullable(),
  nextARace: DashboardNextARaceSchema.nullable(),
  // M2.32.B/D — Default null para compat con backends previos al rollout.
  polarizedRatio4w: z.number().nullable().default(null),
  imbalanceWarn: ImbalanceWarnSchema.nullable().default(null),
});
export type DashboardKpis = z.infer<typeof DashboardKpisSchema>;

/**
 * Extensión opcional del response del `useDashboard` (workouts.ts) — el
 * backend puede o no enviar `kpis`. Cuando el FE quiera tipar el data crudo
 * con kpis, puede importar este schema y hacer `.passthrough()` sobre el
 * existente.
 *
 * Aquí lo exponemos como `DashboardResponseExtraSchema` para que tests
 * puedan validar el shape extendido cuando lo necesiten, sin tocar
 * `workouts.ts`.
 */
export const DashboardResponseExtraSchema = z
  .object({
    kpis: DashboardKpisSchema.optional(),
  })
  .passthrough();

// ---------------------------------------------------------------------------
// Schemas: discipline hours
// ---------------------------------------------------------------------------

export const DISCIPLINE_HOURS_PERIODS = ['week', 'month'] as const;
export type DisciplineHoursPeriod = (typeof DISCIPLINE_HOURS_PERIODS)[number];

export const DisciplineHoursByDisciplineSchema = z.object({
  swim: z.number().nonnegative(),
  bike: z.number().nonnegative(),
  run: z.number().nonnegative(),
  strength: z.number().nonnegative(),
  brick: z.number().nonnegative(),
  other: z.number().nonnegative(),
});
export type DisciplineHoursByDiscipline = z.infer<typeof DisciplineHoursByDisciplineSchema>;

export const DisciplineHoursBucketSchema = z.object({
  bucketStartISO: z.string(),
  bucketEndISO: z.string(),
  label: z.string(),
  totalMin: z.number().nonnegative(),
  targetMin: z.number().nonnegative().nullable(),
  byDiscipline: DisciplineHoursByDisciplineSchema,
});
export type DisciplineHoursBucket = z.infer<typeof DisciplineHoursBucketSchema>;

export const DisciplineHoursResponseSchema = z.object({
  period: z.enum(DISCIPLINE_HOURS_PERIODS),
  buckets: z.array(DisciplineHoursBucketSchema),
});
export type DisciplineHoursResponse = z.infer<typeof DisciplineHoursResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: alerts
// ---------------------------------------------------------------------------

export const ALERT_SEVERITIES = ['info', 'warn', 'danger'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const DashboardAlertSchema = z.object({
  id: z.string(),
  severity: z.enum(ALERT_SEVERITIES),
  title: z.string(),
  detail: z.string(),
  metric: z.string(),
  value: z.number().nullable(),
});
export type DashboardAlert = z.infer<typeof DashboardAlertSchema>;

export const DashboardAlertsResponseSchema = z.object({
  alerts: z.array(DashboardAlertSchema),
});
export type DashboardAlertsResponse = z.infer<typeof DashboardAlertsResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers (mismo patrón que workouts.ts)
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /api/dashboard/discipline-hours?period=week|month&buckets=N
 *
 * El backend valida `buckets` (1..52) y aplica el default según `period`
 * (week → 12, month → 6); por seguridad el cliente también honra esos
 * defaults para query keys deterministas.
 */
export function getDisciplineHours(
  period: DisciplineHoursPeriod,
  buckets?: number,
): Promise<DisciplineHoursResponse> {
  const params = new URLSearchParams();
  params.set('period', period);
  if (typeof buckets === 'number' && Number.isFinite(buckets)) {
    params.set('buckets', String(buckets));
  }
  return getJson(
    `/api/dashboard/discipline-hours?${params.toString()}`,
    DisciplineHoursResponseSchema,
  );
}

/**
 * GET /api/dashboard/alerts
 *
 * Endpoint ligero — devuelve alertas activas (monotonía, ramp, ACWR, etc.)
 * ya priorizadas/ordenadas por el backend. El FE solo filtra las
 * dismisseadas localmente.
 */
export function getDashboardAlerts(): Promise<DashboardAlertsResponse> {
  return getJson('/api/dashboard/alerts', DashboardAlertsResponseSchema);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const DASHBOARD_DISCIPLINE_HOURS_QUERY_KEY = [
  ...DASHBOARD_QUERY_KEY,
  'discipline-hours',
] as const;

export const DASHBOARD_ALERTS_QUERY_KEY = [
  ...DASHBOARD_QUERY_KEY,
  'alerts',
] as const;

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export function useDisciplineHours(
  period: DisciplineHoursPeriod,
  buckets?: number,
): UseQueryResult<DisciplineHoursResponse, Error> {
  return useQuery<DisciplineHoursResponse, Error>({
    queryKey: [...DASHBOARD_DISCIPLINE_HOURS_QUERY_KEY, period, buckets ?? null],
    queryFn: () => getDisciplineHours(period, buckets),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useDashboardAlerts(): UseQueryResult<DashboardAlertsResponse, Error> {
  return useQuery<DashboardAlertsResponse, Error>({
    queryKey: DASHBOARD_ALERTS_QUERY_KEY,
    queryFn: getDashboardAlerts,
    staleTime: 30_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Schemas: Activity Heatmap (M2.17 — shape rico con byDiscipline + actualIds)
// ---------------------------------------------------------------------------

/**
 * Día del heatmap en formato M2.17. El backend de M2.17 amplía el shape del
 * endpoint `/api/stats/activity-heatmap` para incluir, además de `tss` y
 * `count`, `totalMinutes`, `byDiscipline` y `actualIds`.
 *
 * Los campos nuevos son opcionales con default 0 / vector cero / array vacío
 * para mantener compat con respuestas del backend anteriores a M2.17 (el
 * shape original tenía sólo `dateISO`, `tss`, `count`).
 */
export const ActivityHeatmapByDisciplineSchema = z.object({
  swim: z.number().nonnegative().default(0),
  bike: z.number().nonnegative().default(0),
  run: z.number().nonnegative().default(0),
  strength: z.number().nonnegative().default(0),
  brick: z.number().nonnegative().default(0),
  other: z.number().nonnegative().default(0),
});
export type ActivityHeatmapByDiscipline = z.infer<
  typeof ActivityHeatmapByDisciplineSchema
>;

const ZERO_BY_DISCIPLINE: ActivityHeatmapByDiscipline = {
  swim: 0,
  bike: 0,
  run: 0,
  strength: 0,
  brick: 0,
  other: 0,
};

export const ActivityHeatmapDaySchema = z.object({
  dateISO: z.string(),
  tss: z.number().nonnegative(),
  count: z.number().int().nonnegative(),
  totalMinutes: z.number().nonnegative().default(0),
  byDiscipline: ActivityHeatmapByDisciplineSchema.default(ZERO_BY_DISCIPLINE),
  actualIds: z.array(z.number().int()).default([]),
});
export type ActivityHeatmapDay = z.infer<typeof ActivityHeatmapDaySchema>;

export const ActivityHeatmapResponseSchema = z.object({
  days: z.array(ActivityHeatmapDaySchema),
});
export type ActivityHeatmapResponse = z.infer<
  typeof ActivityHeatmapResponseSchema
>;

/**
 * GET /api/stats/activity-heatmap?days=N
 *
 * Cliente nuevo en `lib/dashboard.ts` (M2.17). NO toca `lib/stats.ts` para
 * no modificar el módulo del agente A. La diferencia con `lib/stats.ts` es
 * el shape rico: cada día incluye `totalMinutes`, `byDiscipline` y
 * `actualIds`, indispensables para el rediseño visual + click directo.
 */
export function getActivityHeatmap(days = 182): Promise<ActivityHeatmapResponse> {
  // Cast a `z.ZodType<ActivityHeatmapResponse>` para alinear el tipo de
  // entrada/salida del schema (los `.default(...)` hacen el input opcional
  // pero el output siempre tiene valor concreto). El cast es seguro porque
  // `safeParse` aplica los defaults antes de devolver `data`.
  return getJson(
    `/api/stats/activity-heatmap?days=${encodeURIComponent(String(days))}`,
    ActivityHeatmapResponseSchema as unknown as z.ZodType<ActivityHeatmapResponse>,
  );
}

export const DASHBOARD_ACTIVITY_HEATMAP_QUERY_KEY = [
  ...DASHBOARD_QUERY_KEY,
  'activity-heatmap-v2',
] as const;

export function useActivityHeatmap(
  days = 182,
): UseQueryResult<ActivityHeatmapResponse, Error> {
  return useQuery<ActivityHeatmapResponse, Error>({
    queryKey: [...DASHBOARD_ACTIVITY_HEATMAP_QUERY_KEY, days],
    queryFn: () => getActivityHeatmap(days),
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Helpers Heatmap (M2.17)
// ---------------------------------------------------------------------------

/**
 * Calcula la disciplina dominante de un día (la que aporta más minutos).
 * Si empate, alfabético estable. Devuelve `null` si totalMinutes === 0.
 */
export function dominantDiscipline(
  byDiscipline: ActivityHeatmapByDiscipline,
): DisciplineKey | null {
  let best: DisciplineKey | null = null;
  let bestMin = 0;
  // Orden alfabético para desempate estable.
  const alpha: readonly DisciplineKey[] = [
    'bike',
    'brick',
    'other',
    'run',
    'strength',
    'swim',
  ];
  for (const d of alpha) {
    const m = byDiscipline[d] ?? 0;
    if (m > bestMin) {
      best = d;
      bestMin = m;
    }
  }
  return bestMin > 0 ? best : null;
}

/**
 * Segunda disciplina con más minutos (para el dot de "multi" en celdas con
 * varias disciplinas). Devuelve `null` si sólo hay una con minutos > 0.
 */
export function secondaryDiscipline(
  byDiscipline: ActivityHeatmapByDiscipline,
): DisciplineKey | null {
  const dominant = dominantDiscipline(byDiscipline);
  if (dominant === null) return null;
  const alpha: readonly DisciplineKey[] = [
    'bike',
    'brick',
    'other',
    'run',
    'strength',
    'swim',
  ];
  let second: DisciplineKey | null = null;
  let secondMin = 0;
  for (const d of alpha) {
    if (d === dominant) continue;
    const m = byDiscipline[d] ?? 0;
    if (m > secondMin) {
      second = d;
      secondMin = m;
    }
  }
  return secondMin > 0 ? second : null;
}

/**
 * Cuántas disciplinas tienen minutos > 0 en el día.
 */
export function countActiveDisciplines(
  byDiscipline: ActivityHeatmapByDiscipline,
): number {
  let n = 0;
  for (const d of DISCIPLINE_ORDER) {
    if ((byDiscipline[d] ?? 0) > 0) n += 1;
  }
  return n;
}

/**
 * Opacidad por duración: 5 niveles según totalMinutes.
 *   <10 → 0.25, <30 → 0.45, <60 → 0.7, <90 → 0.85, >=90 → 1.0
 * Si totalMinutes === 0, devolvemos 1 (el componente ya pintará slate).
 */
export function opacityForMinutes(totalMinutes: number): number {
  if (totalMinutes <= 0) return 1;
  if (totalMinutes < 10) return 0.25;
  if (totalMinutes < 30) return 0.45;
  if (totalMinutes < 60) return 0.7;
  if (totalMinutes < 90) return 0.85;
  return 1;
}

// ---------------------------------------------------------------------------
// Helpers DisciplineHoursChart (M2.17 — formato corto label semana)
// ---------------------------------------------------------------------------

const MONTH_NAME_TO_MM: Record<string, string> = {
  ene: '1',
  feb: '2',
  mar: '3',
  abr: '4',
  may: '5',
  jun: '6',
  jul: '7',
  ago: '8',
  sep: '9',
  oct: '10',
  nov: '11',
  dic: '12',
};

/**
 * Reescribe un label de bucket semanal del backend a formato corto `W14 30/3`
 * para reducir solapamiento en el eje X.
 *
 * El backend genera DOS formas posibles:
 *  - Same month:  `"W19 19-25 may"`  (`weekLabel` en `discipline-hours.ts`).
 *  - Cross month: `"W52 28 dic–3 ene"` (en-dash o em-dash entre día y día).
 *
 * Ambos se acortan a `Wnn DD/MM`. Si no matchea ningún patrón conocido
 * (mensual `"May 2026"`, etc.), se devuelve sin cambios.
 */
export function shortenWeekLabel(label: string): string {
  // Caso 1: same month — "W19 19-25 may"
  const sameMonth = /^(W\d+)\s+(\d+)-\d+\s+([A-Za-zÁÉÍÓÚáéíóú]+)$/.exec(label);
  if (sameMonth) {
    const [, week, day, monthName] = sameMonth;
    const mm = MONTH_NAME_TO_MM[(monthName ?? '').toLowerCase().slice(0, 3)];
    if (mm) return `${week} ${day}/${mm}`;
  }
  // Caso 2: cross month — "W52 28 dic–3 ene" (en-dash, em-dash o hyphen).
  const crossMonth =
    /^(W\d+)\s+(\d+)\s+([A-Za-zÁÉÍÓÚáéíóú]+)\s*[-–—]\s*\d+\s+[A-Za-zÁÉÍÓÚáéíóú]+$/.exec(
      label,
    );
  if (crossMonth) {
    const [, week, day, monthName] = crossMonth;
    const mm = MONTH_NAME_TO_MM[(monthName ?? '').toLowerCase().slice(0, 3)];
    if (mm) return `${week} ${day}/${mm}`;
  }
  return label;
}
